import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetSubscriptionPurchasesByShopQuery,
  useGetShopItemsQuery,
} from "../features/shop/shopApiSlice";
import { useGetAdsByUserQuery } from "../features/ads/adsApiSlice";

const PLAN_FALLBACK_TIERS = ["Free", "Basic", "Standard", "Premium"];

const fallbackByTier = (planName, tierValues) => {
  const idx = PLAN_FALLBACK_TIERS.indexOf(planName);
  return tierValues[idx === -1 ? 0 : idx];
};

const isMongoObjectId = (value) =>
  typeof value === "string" && /^[a-fA-F0-9]{24}$/.test(value);

const pickValidId = (...candidates) => {
  for (const raw of candidates) {
    if (!raw || raw === "undefined" || raw === "null") continue;
    return String(raw);
  }
  return null;
};

/** Round money to cents (avoids float noise like 52639.969999999994). */
const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const campaignDays = (startDate, endDate) => {
  const s = String(startDate || "").slice(0, 10);
  const e = String(endDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || !/^\d{4}-\d{2}-\d{2}$/.test(e)) {
    return 0;
  }
  const start = new Date(`${s}T12:00:00`);
  const end = new Date(`${e}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const days = Math.round((end - start) / 86400000);
  return Math.max(days, 1);
};

/**
 * Campaign budget: Remaining = Total − Spent (authoritative).
 * Falls back to stored `remaining` only when totalBudget is missing.
 */
export const resolveAdCampaignBudget = (ad) => {
  const spent = money(ad?.spent ?? ad?.eligibility?.analytics?.spent);
  let total = money(
    ad?.totalBudget ?? ad?.eligibility?.analytics?.totalBudget,
  );
  if (!(total > 0)) {
    const daily = money(ad?.dailyBudget ?? ad?.budget);
    const days = campaignDays(ad?.startDate, ad?.endDate);
    if (daily > 0 && days > 0) total = money(daily * days);
  }
  const storedRemaining = money(
    ad?.remaining ?? ad?.eligibility?.analytics?.remainingBudget,
  );
  let remaining;
  if (total > 0) {
    remaining = money(Math.max(total - spent, 0));
  } else if (
    ad?.remaining != null ||
    ad?.eligibility?.analytics?.remainingBudget != null
  ) {
    remaining = Math.max(storedRemaining, 0);
  } else {
    remaining = 0;
  }
  const resolvedTotal = total > 0 ? total : money(spent + remaining);
  return { spent, total: resolvedTotal, remaining };
};

/** Today's budget — ignore stale dailySpent from a previous calendar day. */
export const resolveAdDailyBudget = (ad, todayStr) => {
  const today =
    todayStr || new Date().toISOString().slice(0, 10);
  const dailyBudget = money(
    ad?.dailyBudget ?? ad?.budget ?? ad?.eligibility?.dailyBudget,
  );
  const day = String(ad?.dailyImpressionDate || "").slice(0, 10);
  const eligSpent = ad?.eligibility?.dailySpent;
  const eligRemaining = ad?.dailyRemaining ?? ad?.eligibility?.dailyRemaining;

  let dailySpent;
  if (eligSpent != null && ad?.eligibility?.dailyBudget != null) {
    // Server snapshot already normalized for "today"
    dailySpent = money(eligSpent);
  } else if (day === today) {
    dailySpent = money(ad?.dailySpent);
  } else {
    dailySpent = 0;
  }

  let dailyRemaining;
  if (eligRemaining != null && Number.isFinite(Number(eligRemaining))) {
    dailyRemaining = money(Math.max(Number(eligRemaining), 0));
  } else {
    dailyRemaining = money(Math.max(dailyBudget - dailySpent, 0));
  }

  return { dailyBudget, dailySpent, dailyRemaining };
};

export const useShopStatistics = () => {
  const { shopid: paramShopId } = useParams();
  const [searchParams] = useSearchParams();
  const loggedInUserId = useSelector(
    (state) => state.user?.name?._id || state.auth?.user?._id || null,
  );

  const openShopId = localStorage.getItem("OpenShopIdForMarketingByMarketer");
  const localShopId = localStorage.getItem("shopid");
  const queryShopId = searchParams.get("shopid");

  const shopRef =
    pickValidId(
      isMongoObjectId(paramShopId) ? paramShopId : null,
      isMongoObjectId(queryShopId) ? queryShopId : null,
      isMongoObjectId(openShopId) ? openShopId : null,
      isMongoObjectId(localShopId) ? localShopId : null,
    ) ||
    pickValidId(paramShopId, queryShopId, openShopId, localShopId);

  const {
    data: subscriptionRes,
    isLoading: isSubLoading,
    isFetching: isSubFetching,
    isError: isSubError,
    error: subError,
    refetch,
  } = useGetSubscriptionPurchasesByShopQuery(shopRef, {
    skip: !shopRef,
    refetchOnMountOrArgChange: true,
  });

  const purchase = subscriptionRes?.data ?? null;

  const shopid =
    purchase?.resolvedShopId ||
    (purchase?.shopid?._id
      ? String(purchase.shopid._id)
      : purchase?.shopid
        ? String(purchase.shopid)
        : null) ||
    (isMongoObjectId(shopRef) ? shopRef : null);

  useEffect(() => {
    if (!shopid || !isMongoObjectId(shopid)) return;
    if (localStorage.getItem("OpenShopIdForMarketingByMarketer") !== shopid) {
      localStorage.setItem("OpenShopIdForMarketingByMarketer", shopid);
    }
  }, [shopid]);

  const ownerId = loggedInUserId ? String(loggedInUserId) : null;

  // Load by owner first — create stores ownerUserId = logged-in user.
  // Shop id on this page often differs from ad.userId, which used to return [].
  const adsQueryArg = useMemo(() => {
    if (ownerId) {
      return {
        ownerUserId: ownerId,
        shopId: shopid || undefined,
      };
    }
    if (shopid) return { shopId: shopid };
    return null;
  }, [ownerId, shopid]);

  const {
    data: adsRes,
    isLoading: isAdsLoading,
    isFetching: isAdsFetching,
    isError: isAdsError,
    error: adsError,
    refetch: refetchAds,
  } = useGetAdsByUserQuery(adsQueryArg, {
    skip: !adsQueryArg,
    pollingInterval: 15000,
    refetchOnMountOrArgChange: true,
  });

  const shopKeySet = useMemo(() => {
    const keys = [
      shopid,
      shopRef,
      purchase?.resolvedShopId,
      purchase?.shopid?._id,
      purchase?.shopid?.shopid,
      purchase?.shopid,
      openShopId,
      localShopId,
      queryShopId,
      paramShopId,
    ]
      .filter(Boolean)
      .map(String);
    return new Set(keys);
  }, [
    shopid,
    shopRef,
    purchase,
    openShopId,
    localShopId,
    queryShopId,
    paramShopId,
  ]);

  const ads = useMemo(() => {
    const list = Array.isArray(adsRes) ? adsRes : [];
    if (!list.length) return [];

    // When loaded by ownerUserId, Flask already returns every campaign for that user.
    // Do NOT re-filter by the page shop id — create can store different shop/profile
    // ids per ad (same owner), which previously hid approved ads (e.g. 2 approved → 1 shown).
    if (ownerId) {
      // Soft-prefer current shop first, but keep the full owner list
      const shopMatched = [];
      const otherOwned = [];
      list.forEach((ad) => {
        const uid = String(ad?.userId || "");
        if (shopKeySet.has(uid)) shopMatched.push(ad);
        else otherOwned.push(ad);
      });
      return shopMatched.length ? [...shopMatched, ...otherOwned] : list;
    }

    const forShop = list.filter((ad) => {
      const uid = String(ad?.userId || "");
      const oid = String(ad?.ownerUserId || "");
      return shopKeySet.has(uid) || shopKeySet.has(oid);
    });

    return forShop.length > 0 ? forShop : list;
  }, [adsRes, shopKeySet, ownerId]);

  const adMetrics = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    const computeEligible = (ad) => {
      if (ad?.eligible === true || ad?.eligibility?.eligible === true) return true;
      if (ad?.eligible === false && ad?.eligibility?.checks) {
        // trust server false only when checks object exists
      }
      const status = String(ad?.status || "").toLowerCase();
      const admin = String(
        ad?.adminStatus || ad?.admin_status || "",
      ).toLowerCase();
      const pay = String(
        ad?.paymentStatus || ad?.payment_status || "",
      ).toLowerCase();
      const start = String(ad?.startDate || "").slice(0, 10);
      const end = String(ad?.endDate || "").slice(0, 10);
      const adminOk = admin === "approved";
      const activeOk = ["active", "running", "scheduled"].includes(status);
      const payOk =
        ["verified", "paid"].includes(pay) ||
        (adminOk && activeOk && !pay);
      const startOk = !start || start <= today;
      const endOk = !end || end >= today;
      const remaining = Number(ad?.remaining);
      const budgetOk =
        ad?.remaining == null ||
        Number.isNaN(remaining) ||
        remaining > 0 ||
        !(Number(ad?.totalBudget) > 0);
      return adminOk && activeOk && payOk && startOk && endOk && budgetOk;
    };

    let impressions = 0;
    let clicks = 0;
    let activeCount = 0;
    let approvedCount = 0;
    let eligibleCount = 0;
    let dailyBudget = 0;
    let dailySpent = 0;
    let dailyRemaining = 0;
    let campaignSpent = 0;
    let campaignRemaining = 0;
    let campaignTotal = 0;
    ads.forEach((ad) => {
      impressions += Number(ad?.impressions) || 0;
      clicks += Number(ad?.clicks) || 0;

      const campaign = resolveAdCampaignBudget(ad);
      campaignSpent = money(campaignSpent + campaign.spent);
      campaignRemaining = money(campaignRemaining + campaign.remaining);
      campaignTotal = money(campaignTotal + campaign.total);

      const dayBudget = resolveAdDailyBudget(ad, today);
      dailyBudget = money(dailyBudget + dayBudget.dailyBudget);
      dailySpent = money(dailySpent + dayBudget.dailySpent);
      dailyRemaining = money(dailyRemaining + dayBudget.dailyRemaining);

      const status = String(ad?.status || "").toLowerCase();
      const admin = String(
        ad?.adminStatus || ad?.admin_status || "",
      ).toLowerCase();
      const isActiveStatus = ["active", "running", "scheduled"].includes(status);
      const isApproved = admin === "approved";
      if (isApproved) approvedCount += 1;
      // "Running" = admin-approved and live status (matches admin approval view)
      if (isApproved && isActiveStatus) activeCount += 1;
      else if (!admin && isActiveStatus) activeCount += 1;
      if (computeEligible(ad)) eligibleCount += 1;
    });
    const conversion =
      impressions > 0 ? Number(((clicks / impressions) * 100).toFixed(2)) : 0;
    // Prefer Total − Spent so remaining never drifts from float / stale fields
    if (campaignTotal > 0) {
      campaignRemaining = money(Math.max(campaignTotal - campaignSpent, 0));
    }
    dailyRemaining = money(Math.max(dailyBudget - dailySpent, 0));
    return {
      impressions,
      clicks,
      conversion,
      ctr: conversion,
      activeCount,
      approvedCount,
      eligibleCount,
      dailyBudget,
      dailySpent,
      dailyRemaining,
      spent: campaignSpent,
      remainingBudget: campaignRemaining,
      totalBudget: campaignTotal,
      computeEligible,
      resolveAdCampaignBudget,
      resolveAdDailyBudget,
    };
  }, [ads]);

  const { data: itemsRes, isLoading: isItemsLoading } = useGetShopItemsQuery(
    { shopid, page: 1, limit: 1 },
    { skip: !shopid },
  );

  const stats = useMemo(() => {
    const planName = purchase?.planId?.planName || "Free";
    const limits = purchase?.planId?.limits || {};

    const postAdsLimit =
      Number(limits.postAdsLimit) > 0
        ? Number(limits.postAdsLimit)
        : fallbackByTier(planName, [5, 10, 20, 50]);

    const itemsLimit =
      Number(limits.itemsLimit) > 0
        ? Number(limits.itemsLimit)
        : fallbackByTier(planName, [10, 50, 150, 500]);

    const shareLimit =
      limits.promoteShopLimit != null && Number(limits.promoteShopLimit) > 0
        ? Number(limits.promoteShopLimit)
        : fallbackByTier(planName, [100, 500, 1000, 2000]);

    const adsUsedCount = ads.length;
    const adsRemaining = Math.max(0, postAdsLimit - adsUsedCount);
    const adsUsedPercentage = Math.min(
      100,
      postAdsLimit > 0 ? Math.round((adsUsedCount / postAdsLimit) * 100) : 0,
    );

    const postsCreated = Number(
      purchase?.timelinePostsCount ?? itemsRes?.totalItems ?? 0,
    );
    const postsRemaining = Math.max(0, itemsLimit - postsCreated);
    const postsUsedPercentage = Math.min(
      100,
      itemsLimit > 0 ? Math.round((postsCreated / itemsLimit) * 100) : 0,
    );

    const facebookShares = Number(purchase?.facebookShares) || 0;
    const otherShares = Number(purchase?.otherShares) || 0;
    const profileShares = Number(
      purchase?.sharedToUsersCount ?? purchase?.profileShares ?? 0,
    );
    const totalShares = profileShares + facebookShares + otherShares;
    const sharesRemaining = Math.max(0, shareLimit - totalShares);
    const sharesUsedPercentage = Math.min(
      100,
      shareLimit > 0 ? Math.round((totalShares / shareLimit) * 100) : 0,
    );

    const startDate = purchase?.createdAt
      ? new Date(purchase.createdAt).toLocaleDateString()
      : "—";

    const endDate = purchase?.createdAt
      ? new Date(
          new Date(purchase.createdAt).setMonth(
            new Date(purchase.createdAt).getMonth() + 1,
          ),
        ).toLocaleDateString()
      : "—";

    return {
      planName,
      postAdsLimit,
      adsUsedCount,
      adsRemaining,
      adsUsedPercentage,
      itemsLimit,
      postsCreated,
      postsRemaining,
      postsUsedPercentage,
      shareLimit,
      totalShares,
      sharesRemaining,
      sharesUsedPercentage,
      startDate,
      endDate,
      facebookShares,
      otherShares,
      profileShares,
    };
  }, [purchase, ads, itemsRes]);

  const noShopSelected = !shopRef && !ownerId;
  const isLoading =
    !noShopSelected &&
    Boolean(shopRef) &&
    (isSubLoading || isSubFetching) &&
    !purchase;

  const isError = !shopRef && !ownerId;

  return {
    shopid: shopid || shopRef,
    shopRef,
    isLoading,
    isAdsLoading: isAdsLoading || isAdsFetching,
    isAdsError,
    isItemsLoading,
    isError,
    isSubError,
    error: isError
      ? { message: "No shop selected. Open statistics from your shop profile." }
      : subError,
    adsError,
    refetch: () => {
      refetch?.();
      refetchAds?.();
    },
    refetchAds,
    ads,
    adMetrics,
    purchase,
    hasPurchaseData: Boolean(purchase),
    ...stats,
  };
};

export default useShopStatistics;
